import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  getAdminReportReview,
  previewAdminReportGlobalMetric,
  publishAdminReportGlobalMetric,
  saveAdminReportThresholdDecision,
  updateAdminReportLearningFutureDisposition,
} from "../../services/api";

const styles = {
  panel: { border: "1px solid var(--teal-border)", borderRadius: 9, padding: 14, background: "var(--bg-card)", marginTop: 12 },
  row: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" },
  spread: { display: "flex", gap: 12, justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap" },
  title: { fontSize: 15, fontWeight: 900, color: "var(--text-primary)" },
  label: { fontSize: 11, fontWeight: 900, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".07em" },
  hint: { fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 },
  button: { border: "1px solid var(--border)", borderRadius: 6, padding: "8px 11px", background: "var(--dashboard-selector-bg)", color: "var(--text-primary)", fontWeight: 850, cursor: "pointer" },
  primary: { border: "none", borderRadius: 6, padding: "8px 12px", background: "var(--teal)", color: "#fff", fontWeight: 850, cursor: "pointer" },
  active: { border: "1px solid var(--teal)", borderRadius: 6, padding: "8px 11px", background: "var(--teal)", color: "#fff", fontWeight: 900, cursor: "pointer" },
  card: { border: "1px solid var(--border)", borderRadius: 8, padding: 11, marginTop: 9, background: "var(--dashboard-selector-bg)" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))", gap: 9, marginTop: 10 },
  input: { width: "100%", boxSizing: "border-box", border: "1.5px solid var(--border)", borderRadius: 6, padding: "8px 9px", background: "var(--bg-card)", color: "var(--text-primary)" },
  textarea: { width: "100%", boxSizing: "border-box", minHeight: 70, resize: "vertical", border: "1.5px solid var(--border)", borderRadius: 6, padding: 9, background: "var(--bg-card)", color: "var(--text-primary)", font: "inherit" },
  badge: { display: "inline-flex", borderRadius: 999, padding: "4px 8px", background: "var(--teal-bg)", color: "var(--teal-dark)", fontSize: 11, fontWeight: 900 },
  backdrop: { position: "fixed", inset: 0, zIndex: 2600, display: "flex", alignItems: "center", justifyContent: "center", padding: 18, background: "rgba(15,23,42,.64)" },
  dialog: { width: "min(620px,94vw)", maxHeight: "88vh", overflow: "auto", border: "1px solid var(--border)", borderRadius: 10, background: "var(--bg-card)", padding: 18, boxShadow: "0 24px 80px rgba(15,23,42,.4)" },
};

function clone(value) {
  return JSON.parse(JSON.stringify(value || {}));
}

function thresholdSummary(threshold = {}) {
  if (threshold.type === "ISO_CODE") return `Good through ${(threshold.goodMax || []).join("/")} · Critical from ${(threshold.failMin || []).join("/")}`;
  if (threshold.type === "BOOL") return "Detected / not detected";
  return `Pass ${threshold.pass ?? "—"} · Warn ${threshold.warn ?? "—"} · Fail ${threshold.fail ?? "—"}`;
}

function selectionMode(differences, selections) {
  if (!differences.length) return "Template Selection";
  const learningCount = differences.filter((item) => selections[item.metricKey] === "learning").length;
  if (learningCount === 0) return "Template Selection";
  if (learningCount === differences.length) return "Learning Lab Selection";
  return "Combined Selection";
}

function editableThreshold(value) {
  const threshold = clone(value);
  delete threshold._conditionalSource;
  return threshold;
}

function ThresholdInputs({ threshold, onChange }) {
  if (threshold.type === "ISO_CODE") {
    return <div style={styles.grid}>
      {["goodMax", "failMin"].map((field) => <fieldset key={field} style={{ border: "1px solid var(--border)", borderRadius: 7 }}>
        <legend style={styles.label}>{field === "goodMax" ? "Good through" : "Critical starting"}</legend>
        <div style={styles.row}>{[0, 1, 2].map((index) => <input key={index} aria-label={`${field} segment ${index + 1}`} type="number" style={{ ...styles.input, width: 82 }} value={threshold[field]?.[index] ?? ""} onChange={(event) => {
          const values = [...(threshold[field] || [0, 0, 0])]; values[index] = Number(event.target.value);
          onChange({ ...threshold, [field]: values });
        }} />)}</div>
      </fieldset>)}
    </div>;
  }
  if (threshold.type === "BOOL") return <div style={styles.hint}>This detected/not-detected metric has no numeric threshold boundaries. Private direction can still be recorded below.</div>;
  return <div style={styles.grid}>{["pass", "warn", "fail"].map((field) => <label key={field}>
    <span style={{ ...styles.label, display: "block", marginBottom: 5 }}>{field}</span>
    <input type="number" style={styles.input} value={threshold[field] ?? ""} onChange={(event) => onChange({ ...threshold, [field]: Number(event.target.value) })} />
  </label>)}</div>;
}

export default function ReportThresholdComparisonPanel({ sampleId, comparison, disabled, metricRequest,
  onMetricRequestHandled, onComparisonChange, onViewChange, onOpenFullTemplate }) {
  const [view, setView] = useState("template");
  const [selections, setSelections] = useState(() => clone(comparison?.draftDecision?.metricSelections));
  const [manualOverrides, setManualOverrides] = useState(() => clone(comparison?.draftDecision?.manualOverrides));
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionMetric, setActionMetric] = useState(null);
  const [editingMetric, setEditingMetric] = useState(null);
  const [editedThreshold, setEditedThreshold] = useState(null);
  const [privateDirection, setPrivateDirection] = useState("");
  const [globalPreview, setGlobalPreview] = useState(null);
  const [publicationNote, setPublicationNote] = useState("");
  const closeRef = useRef(null);

  const differences = comparison?.differences || [];
  const learningAvailable = Boolean(comparison?.learningCandidate && !comparison.learningCandidate.stale);
  const decisionSavingAvailable = Boolean(comparison?.capabilities?.decisionSaving);
  const globalPublishingAvailable = Boolean(comparison?.capabilities?.globalPublishing);
  const currentMode = selectionMode(differences, selections);
  const preview = view === "learning" ? comparison?.learningCandidate
    : view === "final" ? comparison?.resolvedFinal : comparison?.templateCandidate;

  useEffect(() => {
    setSelections(clone(comparison?.draftDecision?.metricSelections));
    setManualOverrides(clone(comparison?.draftDecision?.manualOverrides));
    setDirty(false);
  }, [comparison?.draftDecision?.id, comparison?.resolvedFinal?.decisionHash]);

  useEffect(() => {
    if (!preview?.thresholds) return;
    onViewChange?.({
      thresholds: preview.thresholds,
      templateName: preview.templateName,
      comparisonPreview: true,
      comparisonView: view,
      stagedLearningPreview: view === "learning",
      metricEditingEnabled: decisionSavingAvailable,
    });
  }, [preview?.thresholds, preview?.templateName, view, decisionSavingAvailable, onViewChange]);

  useEffect(() => {
    if (!decisionSavingAvailable || !metricRequest?.metricKey) return;
    setActionMetric(metricRequest.metricKey);
    onMetricRequestHandled?.();
  }, [decisionSavingAvailable, metricRequest, onMetricRequestHandled]);

  useEffect(() => {
    if (actionMetric || editingMetric) closeRef.current?.focus();
  }, [actionMetric, editingMetric]);

  function setAll(source) {
    setSelections(Object.fromEntries(differences.map((item) => [item.metricKey, source])));
    setDirty(true);
  }

  function selectMetric(metricKey, source) {
    setSelections((current) => ({ ...current, [metricKey]: source }));
    setDirty(true);
  }

  async function saveDecision(overrides = manualOverrides, options = {}) {
    setBusy(true); setMessage(null);
    try {
      const response = await saveAdminReportThresholdDecision(sampleId, {
        metricSelections: selections,
        manualOverrides: overrides,
        expectedDecisionHash: comparison.resolvedFinal.decisionHash,
        privateDirection: options.privateDirection,
        eventType: options.eventType || "report_selection_saved",
        metricKey: options.metricKey,
      });
      onComparisonChange?.(response.data.comparison);
      setMessage({ type: "ok", text: options.metricKey ? `${options.metricKey} saved for this report.` : "Final report selection saved." });
      setDirty(false);
      return true;
    } catch (error) {
      setMessage({ type: "err", text: error.response?.data?.error || "Unable to save the report decision." });
      return false;
    } finally { setBusy(false); }
  }

  function openMetric(metricKey) {
    const threshold = comparison?.templateCandidate?.thresholds?.[metricKey];
    if (!threshold) {
      setMessage({ type: "err", text: `${metricKey} does not have an adjustable threshold in this report.` });
      setActionMetric(null);
      return;
    }
    setEditingMetric(metricKey);
    setEditedThreshold(editableThreshold(threshold));
    setPrivateDirection("");
    setGlobalPreview(null);
    setPublicationNote("");
    setActionMetric(null);
  }

  async function saveMetricForReport() {
    const next = { ...manualOverrides, [editingMetric]: editedThreshold };
    const saved = await saveDecision(next, { eventType: "report_metric_saved", metricKey: editingMetric, privateDirection });
    if (saved) { setManualOverrides(next); setEditingMetric(null); }
  }

  async function previewGlobal() {
    setBusy(true); setMessage(null);
    try {
      const response = await previewAdminReportGlobalMetric(sampleId, { metricKey: editingMetric, threshold: editedThreshold });
      setGlobalPreview(response.data);
    } catch (error) {
      setMessage({ type: "err", text: error.response?.data?.error || "Unable to review the global change." });
    } finally { setBusy(false); }
  }

  async function publishGlobal() {
    setBusy(true); setMessage(null);
    try {
      await publishAdminReportGlobalMetric(sampleId, {
        metricKey: editingMetric, threshold: editedThreshold, previewHash: globalPreview.previewHash,
        note: publicationNote, privateDirection,
      });
      const refreshed = await getAdminReportReview(sampleId);
      onComparisonChange?.(refreshed.data.comparison);
      setEditingMetric(null); setGlobalPreview(null);
      setMessage({ type: "ok", text: `${editingMetric} was validated and published globally. Learning Lab proposals from the prior configuration now require regeneration.` });
    } catch (error) {
      setMessage({ type: "err", text: error.response?.data?.error || "Unable to publish the global metric change." });
    } finally { setBusy(false); }
  }

  async function setFutureUse(futureUse) {
    const submissionId = comparison?.learningCandidate?.submissionId;
    if (!submissionId) return;
    setBusy(true);
    try {
      await updateAdminReportLearningFutureDisposition(sampleId, submissionId, futureUse);
      setMessage({ type: "ok", text: `Future Learning Lab disposition recorded as ${futureUse}. This does not affect customer release.` });
    } catch (error) {
      setMessage({ type: "err", text: error.response?.data?.error || "Unable to record the future-use disposition." });
    } finally { setBusy(false); }
  }

  if (!comparison?.featureEnabled) return null;

  return <section style={styles.panel} aria-labelledby="report-comparison-title">
    <div style={styles.spread}>
      <div><div id="report-comparison-title" style={styles.title}>Report Threshold Comparison</div><div style={styles.hint}>{decisionSavingAvailable ? "Preview versions freely, then save an explicit metric-by-metric final selection." : "Read-only rollout: compare Template and Learning Lab results without changing the report."}</div></div>
      <span style={styles.badge}>{currentMode}</span>
    </div>
    <div style={{ ...styles.row, marginTop: 12 }} role="group" aria-label="Report version preview">
      {["template", "learning", "final"].map((key) => <button key={key} type="button" style={view === key ? styles.active : styles.button}
        disabled={disabled || (key === "learning" && !learningAvailable)} onClick={() => setView(key)}>
        {key === "template" ? "Template" : key === "learning" ? "Learning Lab" : "Final Selection"}
      </button>)}
      {!comparison.learningCandidate && <span style={styles.hint}>No Learning Lab proposal is ready.</span>}
      {comparison.learningCandidate?.stale && <span style={{ ...styles.hint, color: "#b91c1c", fontWeight: 800 }}>Proposal is stale and cannot be selected.</span>}
    </div>

    {differences.length > 0 ? <div style={{ marginTop: 13 }}>
      <div style={styles.spread}><div><div style={styles.label}>Differences</div><div style={styles.hint}>{differences.length} proposed metric change{differences.length === 1 ? "" : "s"}</div></div>
        <div style={styles.row}><button type="button" style={styles.button} disabled={!decisionSavingAvailable} onClick={() => setAll("template")}>Use All Template Values</button><button type="button" style={styles.button} disabled={!decisionSavingAvailable || !learningAvailable} onClick={() => setAll("learning")}>Use All Learning Lab Values</button></div>
      </div>
      <div style={styles.grid}>{differences.map((item) => <article key={item.metricKey} style={styles.card}>
        <div style={styles.spread}><strong>{item.metricKey.replaceAll("_", " ")}</strong><span style={styles.badge}>{selections[item.metricKey] === "learning" ? "Learning Lab" : manualOverrides[item.metricKey] ? "Manual" : "Template"}</span></div>
        <div style={{ ...styles.hint, marginTop: 7 }}><strong>Template:</strong> {thresholdSummary(item.templateThreshold)} · {item.templateStatus}</div>
        <div style={{ ...styles.hint, marginTop: 4 }}><strong>Learning Lab:</strong> {thresholdSummary(item.learningThreshold)} · {item.learningStatus}</div>
        <div style={{ ...styles.row, marginTop: 9 }}>
          <label><input type="radio" name={`metric-choice-${item.metricKey}`} disabled={!decisionSavingAvailable} checked={selections[item.metricKey] !== "learning"} onChange={() => selectMetric(item.metricKey, "template")} /> Use Template</label>
          <label><input type="radio" name={`metric-choice-${item.metricKey}`} disabled={!decisionSavingAvailable || !learningAvailable} checked={selections[item.metricKey] === "learning"} onChange={() => selectMetric(item.metricKey, "learning")} /> Use Learning Lab</label>
          <button type="button" style={{ ...styles.button, padding: "5px 8px" }} disabled={!decisionSavingAvailable} onClick={() => setActionMetric(item.metricKey)}>Edit Template Metric</button>
        </div>
      </article>)}</div>
      <div style={{ ...styles.row, marginTop: 12 }}><button type="button" style={styles.primary} disabled={!decisionSavingAvailable || busy || !dirty} onClick={() => saveDecision()}>{busy ? "Saving…" : "Save Final Selection"}</button><span style={styles.hint}>Viewing a version never saves or selects it.</span></div>
    </div> : <div style={{ ...styles.card, ...styles.hint }}>The Template and Learning Lab currently have no threshold differences. Metrics can still be edited directly from the report.<div style={{ marginTop: 9 }}><button type="button" style={styles.primary} disabled={!decisionSavingAvailable || busy} onClick={() => saveDecision()}>{busy ? "Saving…" : "Save Template as Final"}</button></div></div>}

    {comparison.learningCandidate && <details style={{ marginTop: 12 }}><summary style={{ cursor: "pointer", fontWeight: 850 }}>Future Learning Lab consideration (does not block release)</summary><div style={{ ...styles.row, marginTop: 9 }}>
      {["retain", "revise", "dismiss"].map((value) => <button key={value} type="button" style={styles.button} disabled={!decisionSavingAvailable || busy} onClick={() => setFutureUse(value)}>{value[0].toUpperCase() + value.slice(1)}</button>)}
      <span style={styles.hint}>Report use and future learning are recorded separately.</span>
    </div></details>}
    {message && <div role={message.type === "err" ? "alert" : "status"} style={{ marginTop: 10, color: message.type === "err" ? "#b91c1c" : "#166534", fontWeight: 800 }}>{message.text}</div>}

    {actionMetric && <div style={styles.backdrop} role="presentation"><section style={{ ...styles.dialog, width: "min(430px,94vw)" }} role="dialog" aria-modal="true" aria-labelledby="metric-action-title">
      <div id="metric-action-title" style={styles.title}>{actionMetric.replaceAll("_", " ")}</div><p style={styles.hint}>Open only this metric or move to the complete template editor.</p>
      <div style={styles.row}><button type="button" style={styles.primary} onClick={() => openMetric(actionMetric)}>Open this Metric</button><button type="button" style={styles.button} onClick={() => { setActionMetric(null); onOpenFullTemplate?.(actionMetric); }}>Open Full Template</button><button ref={closeRef} type="button" style={styles.button} onClick={() => setActionMetric(null)}>Cancel</button></div>
    </section></div>}

    {editingMetric && editedThreshold && <div style={styles.backdrop} role="presentation"><section style={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="metric-editor-title">
      <div style={styles.spread}><div><div id="metric-editor-title" style={styles.title}>{editingMetric.replaceAll("_", " ")}</div><div style={styles.hint}>Template {comparison.templateCandidate.templateName} · {comparison.templateCandidate.thresholds[editingMetric]?._conditionalSource || "Base template source"}</div></div><button ref={closeRef} type="button" style={styles.button} onClick={() => setEditingMetric(null)}>Close</button></div>
      <div style={{ ...styles.card, marginTop: 12 }}><div style={styles.label}>Report result</div><div style={{ marginTop: 5, fontWeight: 800 }}>Value {comparison.templateCandidate.statuses?.[editingMetric]?.value ?? "—"} · Status {comparison.templateCandidate.statuses?.[editingMetric]?.status || "na"}</div></div>
      <ThresholdInputs threshold={editedThreshold} onChange={(next) => { setEditedThreshold(next); setGlobalPreview(null); }} />
      {editedThreshold.type === "ISO_CODE" && <div style={{ ...styles.hint, marginTop: 8 }}>Particle Count is evaluated component-by-component. A global publication updates its system-wide rule.</div>}
      <label style={{ display: "block", marginTop: 12 }}><span style={styles.label}>Private Learning Lab direction (optional)</span><textarea style={styles.textarea} value={privateDirection} onChange={(event) => setPrivateDirection(event.target.value)} placeholder="Add context for future Learning Lab consideration." /></label>
      {!globalPreview ? <div style={{ ...styles.row, marginTop: 12 }}><button type="button" style={styles.primary} disabled={!decisionSavingAvailable || busy || editedThreshold.type === "BOOL"} onClick={saveMetricForReport}>Save for This Report</button><button type="button" style={styles.button} disabled={!globalPublishingAvailable || busy || editedThreshold.type === "BOOL"} onClick={previewGlobal}>Review Global Change</button>{!globalPublishingAvailable && <span style={styles.hint}>Global publication remains disabled during this rollout stage.</span>}</div>
        : <div style={{ ...styles.card, borderColor: globalPreview.canPublish ? "var(--teal-border)" : "#fecaca" }}>
          <div style={styles.title}>Guarded Global Publication</div><div style={styles.hint}>{globalPreview.source?.label} · {globalPreview.configType} configuration</div>
          <div style={styles.grid}><div><span style={styles.label}>This report</span><div>{globalPreview.beforeStatus} → <strong>{globalPreview.afterStatus}</strong></div></div><div><span style={styles.label}>Affected reports checked</span><div><strong>{globalPreview.affectedReportCount ?? 0}</strong></div></div></div>
          {globalPreview.blockers?.length > 0 && <div role="alert" style={{ color: "#b91c1c", marginTop: 9 }}>{globalPreview.blockers.join(" ")}</div>}
          <label style={{ display: "block", marginTop: 10 }}><span style={styles.label}>Publication note (optional)</span><input style={styles.input} value={publicationNote} onChange={(event) => setPublicationNote(event.target.value)} /></label>
          <div style={{ ...styles.row, marginTop: 12 }}><button type="button" style={styles.primary} disabled={busy || !globalPreview.canPublish} onClick={publishGlobal}>{busy ? "Publishing…" : "Publish Validated Global Change"}</button><button type="button" style={styles.button} onClick={() => setGlobalPreview(null)}>Back to Metric</button></div>
        </div>}
    </section></div>}
  </section>;
}
